/**
 * Utility functions for determining event timing and display logic
 */

/**
 * Get the cutoff time for when events are considered "passed"
 * Events are considered passed on Sunday at 8pm PST (20:00 PST)
 */
export const getEventCutoffTime = () => {
  const now = new Date();

  // Convert current time to PST (UTC-8)
  const pstOffset = -8 * 60; // PST is UTC-8
  const utcTime = now.getTime() + now.getTimezoneOffset() * 60000;
  const pstTime = new Date(utcTime + pstOffset * 60000);

  const currentDay = pstTime.getDay(); // 0 = Sunday, 1 = Monday, etc.

  // Calculate this week's Sunday at 8pm PST
  const cutoffTime = new Date(pstTime);
  cutoffTime.setDate(pstTime.getDate() - currentDay); // Go to this week's Sunday
  cutoffTime.setHours(20, 0, 0, 0); // Set to 8pm PST

  // If we haven't reached Sunday 8pm PST yet this week, use last week's Sunday 8pm PST
  if (pstTime < cutoffTime) {
    cutoffTime.setDate(cutoffTime.getDate() - 7);
  }

  // Convert back to local time
  const localCutoff = new Date(
    cutoffTime.getTime() - pstOffset * 60000 + now.getTimezoneOffset() * 60000,
  );

  return localCutoff;
};

/**
 * Check if an event has passed (after Sunday 8pm PST)
 */
export const hasEventPassed = (eventDateTime) => {
  const eventDate = new Date(eventDateTime);
  const cutoffTime = getEventCutoffTime();
  return eventDate < cutoffTime;
};

/**
 * Check if an event is in the current week (before Sunday 8pm PST cutoff)
 * This is used to show the "current week's event" on the home page
 */
export const isEventThisWeekBeforeCutoff = (eventDateTime) => {
  const eventDate = new Date(eventDateTime);
  const cutoffTime = getEventCutoffTime();

  // Get the start of the current week (last Sunday 8pm PST)
  const weekStart = new Date(cutoffTime);

  // Get the end of the current week (this coming Sunday 8pm PST)
  const weekEnd = new Date(cutoffTime);
  weekEnd.setDate(cutoffTime.getDate() + 7);

  return eventDate >= weekStart && eventDate < weekEnd;
};

/**
 * Get the start and end of the current week (Sunday to Saturday)
 */
export const getCurrentWeekRange = () => {
  const now = new Date();
  const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.

  // Calculate start of week (Sunday)
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - currentDay);
  startOfWeek.setHours(0, 0, 0, 0);

  // Calculate end of week (Saturday)
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  endOfWeek.setHours(23, 59, 59, 999);

  return { startOfWeek, endOfWeek };
};

/**
 * Get the start and end of the following week
 */
export const getFollowingWeekRange = () => {
  const { endOfWeek } = getCurrentWeekRange();

  // Start of following week (next Sunday)
  const startOfFollowingWeek = new Date(endOfWeek);
  startOfFollowingWeek.setDate(endOfWeek.getDate() + 1);
  startOfFollowingWeek.setHours(0, 0, 0, 0);

  // End of following week (next Saturday)
  const endOfFollowingWeek = new Date(startOfFollowingWeek);
  endOfFollowingWeek.setDate(startOfFollowingWeek.getDate() + 6);
  endOfFollowingWeek.setHours(23, 59, 59, 999);

  return { startOfFollowingWeek, endOfFollowingWeek };
};

/**
 * Check if an event is happening this week
 */
export const isEventThisWeek = (eventDateTime) => {
  const eventDate = new Date(eventDateTime);
  const { startOfWeek, endOfWeek } = getCurrentWeekRange();

  return eventDate >= startOfWeek && eventDate <= endOfWeek;
};

/**
 * Check if an event is happening the following week
 */
export const isEventFollowingWeek = (eventDateTime) => {
  const eventDate = new Date(eventDateTime);
  const { startOfFollowingWeek, endOfFollowingWeek } = getFollowingWeekRange();

  return eventDate >= startOfFollowingWeek && eventDate <= endOfFollowingWeek;
};

/**
 * Determine what type of badge/timer to show for an event
 */
export const getEventDisplayType = (eventDateTime) => {
  if (isEventThisWeek(eventDateTime)) {
    return "countdown";
  } else if (isEventFollowingWeek(eventDateTime)) {
    return "upcoming";
  }
  return "none";
};

/**
 * Get the badge text for "upcoming" events
 */
export const getUpcomingBadgeText = (eventDateTime) => {
  const eventDate = new Date(eventDateTime);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const eventDateOnly = new Date(eventDate);
  eventDateOnly.setHours(0, 0, 0, 0);

  const diffTime = eventDateOnly - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  return "Upcoming";
};
